export type BackToSchool2026SaleVariant = 'g1' | 'g2';

/**
 * Every Back to School surface sells the MS in AI, so all three CTAs land on the degree page rather
 * than a checkout deep link — learners read the program details before they pick a plan. Use
 * `getBts2026SaleCtaHref` to append the live coupon so the page renders sale pricing.
 */
export const BTS_2026_SALE_CTA_HREF = '/masters-artificial-intelligence';

/**
 * Hand-drawn underline art sitting behind the price phrase on the homepage card and the
 * `/experience` ATF card (Figma 1856:2053 / 1856:2184). Rendered as a `background-image` on the
 * phrase span rather than an absolutely positioned layer, so it tracks the text width across
 * breakpoints and price variants.
 */
export const BTS_2026_SALE_UNDERLINE_IMAGE = '/images/promotions/back-to-school-2026/homepage-underline.png';

/**
 * Back to School 2026 creative, per market tier. Bespoke to this campaign: the headline stacks an
 * accented line 1 over a white line 2, the CTA fill/label colors differ per surface, and the
 * catalog banner sits on a gradient rather than a flat fill. Summer 2026's `SitewideSalePromoCard`
 * / `SitewideSaleBanner` do not fit that shape, so this campaign ships its own components.
 */
export type BackToSchool2026SaleVariantConfig = {
  catalogBackgroundGradient: string;
  catalogBg: string;
  catalogBgMobile: string;
  catalogBorderColor: string;
  catalogCtaColor: string;
  catalogCtaLabel: string;
  /** Catalog line 1 renders white; line 2 takes `headlineAccentColor` (inverted vs. the homepage). */
  catalogHeadlineLine1: string;
  catalogHeadlineLine2: string;
  experienceAtfBg: string;
  experienceAtfCtaLabel: string;
  experienceAtfDescription: string;
  experienceAtfObjectFit: 'contain' | 'cover';
  experienceAtfObjectPosition: string;
  headlineAccentColor: string;
  headlineLine1: string;
  headlineLine2: string;
  /** Substring of `headlineLine2` that sits above `BTS_2026_SALE_UNDERLINE_IMAGE`. */
  headlineUnderlinePhrase: string;
  homepageBackgroundColor: string;
  homepageBg: string;
  homepageBgMobile: string;
  homepageCtaColor: string;
  homepageCtaLabel: string;
  homepageCtaTextColor: string;
  homepageMobileObjectFit: 'contain' | 'cover';
  homepageMobileObjectPosition: string;
  homepageObjectFit: 'contain' | 'cover';
  homepageObjectPosition: string;
  homepageSubtitle: string;
  homepageSubtitleColor: string;
  homepageTitleColor: string;
};

const BTS_2026_SALE_SHARED_CONFIG = {
  catalogBackgroundGradient: 'linear-gradient(101deg, #002375 0%, #031643 50%)',
  catalogBg: '/images/promotions/back-to-school-2026/catalog-bg.png',
  catalogBgMobile: '/images/promotions/back-to-school-2026/catalog-bg-mobile.png',
  catalogBorderColor: '#915B14',
  catalogCtaColor: '#175CFF',
  catalogCtaLabel: 'Enroll now',
  catalogHeadlineLine1: 'Back to school sale for a limited time only.',
  experienceAtfBg: '/images/promotions/back-to-school-2026/experience-atf-bg.png',
  experienceAtfCtaLabel: 'Explore MSc in AI',
  experienceAtfDescription: 'A degree designed for the next phase of your career.',
  experienceAtfObjectFit: 'cover',
  experienceAtfObjectPosition: 'center',
  headlineAccentColor: '#EDA93B',
  headlineLine1: 'Back to school sale',
  homepageBackgroundColor: '#0A0B0F',
  homepageBg: '/images/promotions/back-to-school-2026/homepage-bg.png',
  homepageBgMobile: '/images/promotions/back-to-school-2026/homepage-bg-mobile.png',
  homepageCtaColor: '#EDA93B',
  homepageCtaLabel: 'Save Now',
  homepageCtaTextColor: '#031643',
  homepageMobileObjectFit: 'cover',
  homepageMobileObjectPosition: 'right top',
  homepageObjectFit: 'cover',
  homepageObjectPosition: '58% center',
  homepageSubtitle: 'Save for a limited time on a flexible, project-based AI degree built for career growth.',
  homepageSubtitleColor: '#CECFD4',
  homepageTitleColor: 'white',
} as const satisfies Partial<BackToSchool2026SaleVariantConfig>;

export const BTS_2026_SALE_VARIANTS: Record<BackToSchool2026SaleVariant, BackToSchool2026SaleVariantConfig> = {
  g1: {
    ...BTS_2026_SALE_SHARED_CONFIG,
    catalogHeadlineLine2: 'Earn your Master’s in AI for under $2.5K USD.',
    headlineLine2: 'Earn a Master’s in AI for under $2.5K USD',
    headlineUnderlinePhrase: 'under $2.5K',
  },
  g2: {
    ...BTS_2026_SALE_SHARED_CONFIG,
    catalogHeadlineLine2: 'Earn your Master’s in AI for under $1.6K USD.',
    headlineLine2: 'Earn a Master’s in AI for under $1.6K USD',
    headlineUnderlinePhrase: 'under $1.6K',
  },
};

/**
 * MS in AI landing page link with the live coupon appended as `?coupon=<promoCode>`. The coupon also
 * auto-applies from the `COUPON_COOKIE` that `usePromotion` writes; the query param is a fallback
 * in case auto-apply does not run. When absent, the bare landing page link is returned.
 */
export function getBts2026SaleCtaHref(promoCode?: string | null): string {
  if (!promoCode) {
    return BTS_2026_SALE_CTA_HREF;
  }
  const params = new URLSearchParams({ coupon: promoCode });
  return `${BTS_2026_SALE_CTA_HREF}?${params.toString()}`;
}
